/**
 * Alberta land title transfer (registration) fee.
 * Alberta has no provincial land transfer tax; this is the title registration charge only.
 * Formula: $50 base + $5.00 per $5,000 of property value (Land Titles Registration Levy, Oct 2024+).
 */

import { calculateOntarioLandTransferTax } from './ontarioLandTransferTax';

export function calculateAlbertaLandTitleTransferFee(purchasePrice: number): number {
  return 50 + Math.round((purchasePrice / 5000) * 5);
}

export interface AlbertaClosingCostEstimates {
  landTitleFee: number;
  ontarioProvincialLttComparison: number;
  ontarioTorontoCombinedLttComparison: number;
  lttSavingsVsOntario: number;
  lttSavingsVsToronto: number;
  cmhcPremiumPst: number;
  legalFees: number;
  titleInsurance: number;
  homeInspection: number;
  closingAdjustments: number;
  totalBeyondDownPaymentFthb: number;
  totalBeyondDownPaymentRepeat: number;
}

export function albertaClosingCostEstimates(opts: {
  purchasePrice: number;
  cmhcPremium: number;
  legalFees?: number;
  titleInsurance?: number;
  homeInspection?: number;
  closingAdjustments?: number;
}): AlbertaClosingCostEstimates {
  const {
    purchasePrice,
    cmhcPremium: _cmhcPremium,
    legalFees = 1750,
    titleInsurance = 325,
    homeInspection = 500,
    closingAdjustments = 1750,
  } = opts;

  const landTitleFee = calculateAlbertaLandTitleTransferFee(purchasePrice);
  const ontarioProvincialLttComparison = calculateOntarioLandTransferTax(purchasePrice);
  const ontarioTorontoCombinedLttComparison = ontarioProvincialLttComparison * 2;
  const lttSavingsVsOntario = ontarioProvincialLttComparison - landTitleFee;
  const lttSavingsVsToronto = ontarioTorontoCombinedLttComparison - landTitleFee;
  const cmhcPremiumPst = 0;

  const fixedCash =
    cmhcPremiumPst + legalFees + titleInsurance + homeInspection + closingAdjustments;

  return {
    landTitleFee,
    ontarioProvincialLttComparison,
    ontarioTorontoCombinedLttComparison,
    lttSavingsVsOntario,
    lttSavingsVsToronto,
    cmhcPremiumPst,
    legalFees,
    titleInsurance,
    homeInspection,
    closingAdjustments,
    totalBeyondDownPaymentFthb: landTitleFee + fixedCash,
    totalBeyondDownPaymentRepeat: landTitleFee + fixedCash,
  };
}
